import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Label } from '@heidi/design-system/label';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Plus } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import {
  WORK_ITEMS_BY_KIND,
  type ObjectWorkspaceObject,
  type ObjectWorkspaceWorkEngagement,
  type ObjectWorkspaceWorkItem,
} from './objectWorkspace.data';

const WORK_COMPLETION_STYLE_MS = 200;
const WORK_ITEM_LAYOUT_SPRING = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.68,
};

interface ObjectWorkspaceWorkContentProps {
  kind: ObjectWorkspaceObject['kind'];
  onWorkEngage?: (work: ObjectWorkspaceWorkEngagement) => void;
}

export function ObjectWorkspaceWorkContent({
  kind,
  onWorkEngage,
}: ObjectWorkspaceWorkContentProps): JSX.Element {
  const intl = useIntl();
  const reducedMotion = useReducedMotion() ?? false;
  const initialItems = WORK_ITEMS_BY_KIND[kind];
  const [items, setItems] = useState(() => [...initialItems]);
  const [completedIds, setCompletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [completedOrder, setCompletedOrder] = useState<readonly string[]>([]);
  const completedIdsRef = useRef(new Set<string>());
  const scheduledIdsRef = useRef(new Set<string>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const nextLocalItemNumberRef = useRef(initialItems.length + 1);

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
      scheduledIdsRef.current.clear();
    },
    []
  );

  const toggleItem = (item: ObjectWorkspaceWorkItem, checked: boolean) => {
    if (checked) {
      if (completedIdsRef.current.has(item.id)) return;

      completedIdsRef.current.add(item.id);
      setCompletedIds(new Set(completedIdsRef.current));
      onWorkEngage?.({ id: item.id, title: intl.formatMessage(item.label) });

      if (reducedMotion) {
        setCompletedOrder((current) => [...current, item.id]);
        return;
      }

      scheduledIdsRef.current.add(item.id);
      const reorder = setTimeout(() => {
        scheduledIdsRef.current.delete(item.id);
        timersRef.current.delete(item.id);
        setCompletedOrder((current) =>
          current.includes(item.id) ? current : [...current, item.id]
        );
      }, WORK_COMPLETION_STYLE_MS);
      timersRef.current.set(item.id, reorder);
      return;
    }

    if (!completedIdsRef.current.delete(item.id)) return;

    const reorder = timersRef.current.get(item.id);
    if (reorder) clearTimeout(reorder);
    timersRef.current.delete(item.id);
    scheduledIdsRef.current.delete(item.id);
    setCompletedIds(new Set(completedIdsRef.current));
    setCompletedOrder((current) => current.filter((id) => id !== item.id));
  };

  const addWork = () => {
    const nextItem: ObjectWorkspaceWorkItem = {
      id: `local-work-${nextLocalItemNumberRef.current}`,
      label: messages.newWorkItem,
      timestamp: messages.justNow,
    };
    nextLocalItemNumberRef.current += 1;
    setItems((current) => [...current, nextItem]);
  };

  const reorderedIds = new Set(completedOrder);
  const orderedItems = [
    ...items.filter((item) => !reorderedIds.has(item.id)),
    ...completedOrder.flatMap((id) => {
      const item = items.find((candidate) => candidate.id === id);
      return item ? [item] : [];
    }),
  ];

  return (
    <section
      aria-label={intl.formatMessage(messages.workHeading)}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        {orderedItems.map((item) => {
          const checkboxId = `object-work-${item.id}`;
          const timestampId = `${checkboxId}-timestamp`;
          const isCompleted = completedIds.has(item.id);
          const hasReordered = reorderedIds.has(item.id);

          return (
            <m.div
              key={item.id}
              data-completion-state={
                hasReordered ? 'completed' : isCompleted ? 'completing' : undefined
              }
              layout={reducedMotion ? false : 'position'}
              transition={reducedMotion ? { duration: 0 } : WORK_ITEM_LAYOUT_SPRING}
              className="motion-reduce:transform-none"
            >
              <Label
                htmlFor={checkboxId}
                className={cn(
                  'flex items-start gap-3 rounded-lg px-3 py-2.5 transition-[background-color,opacity] duration-200 ease-out motion-reduce:transition-none',
                  'hover:bg-surface-hover active:bg-surface-pressed',
                  isCompleted && 'opacity-60'
                )}
              >
                <Checkbox
                  id={checkboxId}
                  checked={isCompleted}
                  aria-label={intl.formatMessage(item.label)}
                  aria-describedby={timestampId}
                  className="mt-0.5 shrink-0"
                  onCheckedChange={(checked) => toggleItem(item, checked === true)}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={cn(
                      typographyVariant({ variant: 'p2' }),
                      'transition-colors duration-200 ease-out motion-reduce:transition-none',
                      isCompleted
                        ? 'text-text-secondary line-through'
                        : 'text-text-primary'
                    )}
                  >
                    {intl.formatMessage(item.label)}
                  </span>
                  <span
                    id={timestampId}
                    className={cn(
                      typographyVariant({ variant: 'caption' }),
                      'text-text-tertiary'
                    )}
                  >
                    {intl.formatMessage(item.timestamp)}
                  </span>
                </span>
              </Label>
            </m.div>
          );
        })}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        flush="start"
        className="w-fit text-text-secondary"
        onClick={addWork}
      >
        <Plus aria-hidden />
        {intl.formatMessage(messages.addWork)}
      </Button>
    </section>
  );
}
